import React from 'react';
import { Avatar, Box, Grid, IconButton, Typography } from '@mui/material';
import { useResourceShares } from 'hooks/share/useResourceShare';
import CloseIcon from '@mui/icons-material/Close';
import { SparkDropdown } from 'sharedComponents/SparkForm';
import { useResourceAccessLevels } from 'hooks/share/useResourceAccessLevels';
import { ResourceShare } from 'types/Share/ResourceShares';
import { useUpdateResourceShare } from 'hooks/share/useUpdateResourceShare';
import { useDeleteResourceShare } from 'hooks/share/useDeleteResourceShare';
import { SparkButton } from 'sharedComponents/UI';
import { useResendInvite } from 'hooks/share/useResendInvite';

type ManageCollaboratorProps = {
  resourceID: string;
  resourceTypeID: string;
  accessLevelShares: ResourceShare[];
};
function ManageCollaborator({
  resourceID,
  resourceTypeID,
  accessLevelShares: collaborators,
}: ManageCollaboratorProps) {
  const { getResourceShareUser } = useResourceShares(
    resourceID,
    resourceTypeID
  );
  const { accessLevelDropdownOptions, getResourceAccessLevelID } =
    useResourceAccessLevels(resourceTypeID);

  const {
    mutateAsync: updateResourceShare,
    isLoading: isUpdatingResourceShare,
  } = useUpdateResourceShare();
  const {
    mutateAsync: deleteResourceShare,
    isLoading: isDeletingResourceShare,
  } = useDeleteResourceShare(undefined, resourceID, resourceTypeID);
  const { mutateAsync: resendInvite, isLoading: isLoadingResendInvite } =
    useResendInvite();

  const getName = (userID: string | null, email?: string | null) => {
    if (!userID) {
      return email;
    }

    const user = getResourceShareUser(userID);

    if (!user) {
      return email;
    }

    return user.attributes.name;
  };

  const getProfileImageURL = (userID: string | null) => {
    if (!userID) {
      return;
    }

    const user = getResourceShareUser(userID);

    if (!user) {
      return;
    }

    return user.attributes.profilePictureURL;
  };

  const handleDelete = (resourceShareID: string) => {
    deleteResourceShare({ id: resourceShareID });
  };

  const handleResendInvite = (resourceShareID: string) => {
    resendInvite({ id: resourceShareID });
  };

  const handleAccessLevelChange = (
    resourceShareID: string,
    accessLevelID: string
  ) => {
    updateResourceShare({
      id: resourceShareID,
      resourceAccessLevelID: getResourceAccessLevelID(
        resourceTypeID,
        accessLevelID
      ),
    });
  };

  const isLoading =
    isDeletingResourceShare || isUpdatingResourceShare || isLoadingResendInvite;

  return (
    <Grid container padding={2} gap={1}>
      <Grid container gap={1}>
        {collaborators?.map((collaborator) => (
          <Grid item container xs={12} justifyContent='space-between'>
            <Box display='flex' alignItems='center' gap={2} flex={1}>
              <Avatar
                key={collaborator.id}
                alt={collaborator.attributes.createdAt}
                src={getProfileImageURL(collaborator.attributes.userID) || ''}
                sx={{
                  bgcolor: (theme) => theme.palette.primary.light,
                  color: (theme) => theme.palette.primary.main,
                }}
              >
                <Typography>
                  {getName(
                    collaborator.attributes.userID,
                    collaborator.attributes.email
                  )
                    ?.slice(0, 2)
                    .toUpperCase()}
                </Typography>
              </Avatar>
              <Box>
                <Typography>
                  {getName(
                    collaborator.attributes.userID,
                    collaborator.attributes.email
                  )}
                </Typography>
                {!collaborator.attributes.userID && (
                  <SparkButton
                    sx={{ padding: 0, justifyContent: 'flex-start' }}
                    onClick={() => handleResendInvite(collaborator.id)}
                    disabled={isLoading}
                  >
                    <Typography textTransform='none' variant='body2'>
                      Resend
                    </Typography>
                  </SparkButton>
                )}
              </Box>
            </Box>
            <Box
              display='flex'
              alignItems='center'
              gap={2}
              width='50%'
              justifyContent='flex-end'
            >
              <SparkDropdown
                name={collaborator.id}
                label=''
                onChange={(value) =>
                  handleAccessLevelChange(collaborator.id, value as string)
                }
                disableGutter={true}
                size={12}
                menuItemProps={{ divider: false }}
                disabled={isLoading}
              >
                {accessLevelDropdownOptions}
              </SparkDropdown>
              <IconButton
                onClick={() => handleDelete(collaborator.id)}
                disabled={isLoading}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Grid>
  );
}

export default ManageCollaborator;
